<template>
  <v-skeleton-loader
    type="table-thead, table-tbody"
    width="100%"
    :loading="marketTableLoader"
    boilerplate
  >
    <v-container class="prices-list-table pa-0 " fluid>
      <v-card width="100%" color="gray10 ">
        <v-row no-gutters class="justify-md-space-between justify-center">
          <v-col cols="12" class="pa-4">
            <v-text-field
              v-model="searchKey"
              :placeholder="$t('common.searchPlaceHolder')"
              solo
              flat
              hide-details
              dense
              class="custom-table-input"
              height="38"
              full-width
              clearable
              @input="handleSearch"
              @click:clear="searchKey = ''"
              clear-icon="$deleteSearch"
              background-color="field"
            >
              <template v-slot:prepend-inner>
                <v-icon class="ms-2" size="24" :clr="'var(--v-gray1-base)'"
                  >$search
                </v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" class="mt-4">
            <v-tabs
              v-model="activeTab"
              class=""
              height="38"
              background-color="transparent"
              show-arrows
            >
              <v-tabs-slider></v-tabs-slider>
              <v-tab v-if="isAuth" @click="changeFavorTab">
                {{ $t("market.custom") }}
              </v-tab>
              <v-tab
                v-for="(item, index) in baseCoinType"
                :key="index"
                @click="changeTab(item)"
              >
                {{
                  item.coinType === "VIRTUAL"
                    ? $t("marketsPage.demoMarket")
                    : item.localedUnit
                }}
              </v-tab>
            </v-tabs>
          </v-col>
          <v-col cols="12" class="mt-4">
            <v-data-table
              class="custom-price-data-table gray10"
              :no-data-text="$t('common.nodata')"
              :header-props="{ 'sort-icon': '$sortIcon' }"
              fixed-header
              :headers="headers"
              :items="items"
              hide-default-footer
              disable-pagination
              ref="table"
              @click:row="changeRoutePair"
              :mobile-breakpoint="100"
            >
              <template v-slot:item.symbol="{ item }">
                <div class="px-2 d-flex align-center justify-end">
                  <span
                    class="pt-1 font-Regular-12"
                    style="cursor: pointer"
                    :class="{ 'ms-5': !isAuth }"
                  >
                    {{ item.symbol | splitCoin }}
                  </span>
                  <v-btn icon @click="toggleFavorite(item)" v-if="isAuth">
                    <v-icon
                      :class="[
                        'favorite-icon',
                        {
                          'svg-color-secondary': item.isFavor
                        }
                      ]"
                      class="ms-2"
                      style="cursor: pointer"
                      size="20"
                      >$favoriteStar
                    </v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:item.close="{ item }">
                <span class="font-Regular-12">
                  {{
                    $thousandMask(
                      item.close,
                      pairCoinScales[item.symbol].frontBaseCoinScale
                    )
                  }}
                </span>
              </template>
              <template v-slot:item.chg="{ item }">
                <div
                  dir="ltr"
                  class="px-2 mb-0 d-flex justify-end"
                  :class="
                    item.chg >= 0
                      ? item.chg === 0
                        ? ''
                        : 'green--text'
                      : 'red--text'
                  "
                >
                  <span v-if="item.chg > 0">+</span>
                  <span>{{ (item.chg * 100).toFixed(2) }}%</span>
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </v-skeleton-loader>
</template>

<script>
import $thousandMask from "@/mixins/thousandMask";
import { ExchangeService, MarketMarketService } from "@/services";
import debounce from "lodash-es/debounce";
import { mapActions, mapGetters } from "vuex";
import * as types from "@/store/types";
import { authMixin } from "@/mixins/authMixin";

const Stomp = require("stompjs");
const SockJS = require("sockjs-client");

export default {
  mixins: [$thousandMask, authMixin],
  data() {
    return {
      headers: [
        {
          text: this.$t("exchange.dayChange"),
          value: "chg",
          sortable: true,
          align: "start",
          width: 120
        },
        {
          text: this.$t("exchange.lastPrice"),
          value: "close",
          sortable: true,
          align: "center",
          width: 120
        },

        {
          text: this.$t("exchange.coin"),
          value: "symbol",
          sortable: true,
          align: "end",
          width: 120
        }
      ],
      subscriptions: [],
      marketTableLoader: false,
      items: [],
      favoriteList: [],
      searchKey: "",
      currentTab: null,
      activeTab: 0,
      baseCoinType: [],
      pairCoinScales: []
    };
  },
  filters: {
    splitCoin(pair) {
      return pair.split("/")[0];
    }
  },
  computed: {
    ...mapGetters({
      currentCoin: types.marketExchange.getters.GET_CURRENT_COIN
    })
  },
  watch: {
    "currentCoin.isFavor": {
      deep: true,
      handler(newVal) {
        if (!this.currentTab && this.isAuth) {
          this.getFavor();
        } else {
          this.items = this.items.map(item => {
            if (item.symbol === this.currentCoin.symbol) {
              return { ...item, isFavor: newVal };
            }
            return item;
          });
        }
      }
    }
  },
  mounted() {
    this.getPairCoinScales();
    this.getBaseSymbols();
  },
  created() {
    // Create debounced methods in created hook to ensure proper 'this' binding
    this.debounceSearchFavors = debounce(this.getFavor, 500);
    this.debounceSearch = debounce(this.getSymbol, 500);
  },
  methods: {
    ...mapActions({
      setCurrentCoin: types.marketExchange.actions.SET_CURRENT_COIN
    }),
    getPairCoinScales() {
      MarketMarketService.getPairCoinScales().then(res => {
        this.pairCoinScales = res.data.data;
      });
    },
    toggleFavorite(item) {
      let payload = {
        symbol: item.symbol
      };
      const api = !item.isFavor
        ? ExchangeService.addFavoredMarkets
        : ExchangeService.deleteFavoredMarkets;
      api(payload)
        .then(response => {
          if (response.data.code === 0) {
            this.$toast.success(
              !item.isFavor
                ? this.$t("marketsPage.addToFavorites")
                : this.$t("marketsPage.removeFavorites")
            );
            this.setCurrentCoin({
              ...this.currentCoin,
              isFavor: !this.currentCoin.isFavor
            });
            if (!this.currentTab) {
              this.getFavor();
            } else {
              this.items = this.items.map(obj => {
                if (obj.symbol === item.symbol) {
                  return { ...obj, isFavor: !item.isFavor };
                }
                return obj;
              });
            }
          }
        })
        .catch(err => {
          this.$toast.error(err.message);
        });
    },
    changeFavorTab() {
      this.searchKey = "";
      this.currentTab = null;
      this.getFavor();
    },
    changeTab(item) {
      this.currentTab = item;
      this.searchKey = "";
      this.getSymbol();
    },
    handleSearch() {
      if (!this.currentTab) {
        this.debounceSearchFavors();
      } else {
        this.debounceSearch();
      }
    },
    debounceSearchFavors: debounce(() => {
      this.getFavor();
    }, 500),
    debounceSearch: debounce(() => {
      this.getSymbol();
    }, 500),
    getFavor() {
      this.items = [];
      this.favoriteList = [];
      ExchangeService.getFavoredMarkets({ symbol: this.searchKey }).then(
        response => {
          if (response.data.data?.length > 0) {
            this.items = response.data.data;
            this.items.forEach(item => {
              item.isFavor = true;
            });
            this.favoriteList = response.data.data;

            this.startWebsocket();
          }
        }
      );
    },
    getSymbol() {
      MarketMarketService.getSymbolsThumbBase({
        baseSymbol: this.currentTab.unit,
        coinSymbol: this.searchKey.toUpperCase()
      }).then(response => {
        this.items = response.data;
        this.items.forEach(item => {
          item.isFavor = this.favoriteList.some(
            fav => fav.symbol === item.symbol
          );
        });
        if (this.$route.params.pair) {
          let currentItem = this.items.find(
            obj => obj.symbol === this.$route.params.pair.replace("_", "/")
          );
          if (currentItem) {
            this.setCurrentCoin(currentItem);
            this.checkIsFavorite();
          }
        }
        this.startWebsocket();
      });
    },
    checkIsFavorite() {
      ExchangeService.getFavoredMarkets().then(response => {
        const favFount = response.data.data.find(
          item => item.symbol === this.$route.params.pair.replace("_", "/")
        );
        if (favFount) {
          this.setCurrentCoin({
            ...this.currentCoin,
            isFavor: true
          });
        }
      });
    },
    getBaseSymbols() {
      MarketMarketService.getBaseSymbolsList().then(response => {
        this.baseCoinType = response.data.data;
        if (this.$route.params.pair) {
          let item = this.baseCoinType.find(
            obj =>
              obj.unit === this.$route.params.pair.split("_")[1].toUpperCase()
          );
          this.activeTab = !this.isAuth
            ? this.baseCoinType.findIndex(obj => obj.unit === item.unit)
            : this.baseCoinType.findIndex(obj => obj.unit === item.unit) + 1;
          this.changeTab(item);
        }
      });
    },
    startWebsocket() {
      // unsubscribe last subscribe
      this.subscriptions.forEach(sub => sub.unsubscribe());
      this.subscriptions = [];

      let stompClient = null;
      let socket = new SockJS(
        `${process.env.VUE_APP_BASE_MARKET}/market/market-ws`
        // `https://market-api.excoino.com/market/market-ws`
      );
      stompClient = Stomp.over(socket);
      stompClient.debug = false;
      stompClient.connect({}, () => {
        //subscribe to price change news
        this.items.forEach(item => {
          const subscription = stompClient.subscribe(
            `/topic/market/thumb/${item.symbol}`,
            msg => {
              let resp = JSON.parse(msg.body);
              item.close = resp.close;
              item.chg = resp.chg;
              item.high = resp.high;
              item.low = resp.low;
              item.twentyFourHourTurnover = resp.twentyFourHourTurnover;
              item.volume = parseInt(resp.volume);
              if (item.symbol === this.currentCoin.symbol) {
                this.setCurrentCoin(resp);
              }
            }
          );
          // push subscription for Unsubscription
          this.subscriptions.push(subscription);
        });
      });
    },
    changeRoutePair(item) {
      this.setCurrentCoin(item);
      this.$router.push({
        path: `/exchange/${item.symbol.replace("/", "_")}`
      });
    }
  }
};
</script>

<style lang="scss">
.market-tabs {
  .v-item-group {
    .v-slide-group__prev,
    .v-slide-group__next {
      display: none !important;
    }
  }
}
</style>
